import { Monitor, Moon, Sun } from 'lucide-react';
import type { Theme } from '@taskflow/shared';
import { cn } from '../lib/cn';

export interface ThemeToggleProps {
  value: Theme;
  onChange: (theme: Theme) => void;
  className?: string;
}

const OPTIONS: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * Three-way theme control.
 *
 * "System" is a distinct choice rather than an implicit default, because a user
 * who has deliberately chosen light while their OS is dark should keep light —
 * and should be able to hand control back to the OS later.
 *
 * Deliberately stateless: the selected value and persistence live in the web
 * app's ThemeContext, keeping this package free of app state.
 */
export function ThemeToggle({ value, onChange, className }: ThemeToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-sm bg-surface-sunken p-0.5',
        className,
      )}
    >
      {OPTIONS.map((option) => {
        const Icon = option.icon;
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            title={option.label}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex size-7 items-center justify-center rounded-sm',
              'transition-colors duration-[--duration-micro]',
              selected
                ? 'bg-surface-raised text-text-primary shadow-e1'
                : 'text-text-tertiary hover:text-text-primary',
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
